import {announce} from '@github-ui/aria-live'
import {AttachmentErrorBanner} from '@github-ui/copilot-shared-ui/components/AttachmentErrorBanner'
import {SlashCommandMenu} from '@github-ui/copilot-shared-ui/components/SlashCommandMenu'
import {isCloudSessionStoreEnabled} from '@github-ui/copilot-shared-ui/utils/cloud-session-store-flag'
import {ChatInputValidationError} from '@github-ui/copilot-chat-state/validation-error'
import {SUBMIT_ERROR_MESSAGE} from '@github-ui/ahp-session-client/submission-error'
import {
  createAhpChatStartRequestWithProvisioningTarget,
  type AhpProvisioningTarget,
  type AhpSpaceSelection,
  type DirectTurnTelemetrySeed,
  type PreparedPrompt,
} from '@github-ui/ahp-session-client/contracts'
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type Ref,
  type SetStateAction,
} from 'react'

import {CopilotComposerInput} from '../task/CopilotComposerInput'
import {isSandboxComposedDraft, isSessionTokenMeterApplicable} from '../task/use-environment-selection'

import {EntitlementTokenUsageMeter} from '../shared/TokenUsageMeterSurface'
import {useMarkdownImageHandling} from '../task/use-markdown-image-handling'
import {useTaskComposer, type TaskComposerTargetState} from '../task/use-task-composer'
import type {TaskComposerScopeState} from '../task/use-task-composer-scope'
import type {EnvironmentOption} from '../task/environment-strip/environment-option'
import {availableAgentModes, resolveAvailableAgentMode, type ChatLaunch, type CreateTaskReference} from '../task/shared'
import type {HandleUserSubmit} from '../task/user-submit'
import type {Repository} from '@github-ui/current-repository'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import {APPROVAL_MODES} from '@github-ui/ahp-relay/permission-mode'

import type {ComposerHandle, NewSessionConfiguration, NewSessionController} from '../props'
import type {SharedSessionDraft} from './use-shared-session-draft'

interface BranchState {
  selectedBranch: string
  setSelectedBranch: Dispatch<SetStateAction<string>>
}

interface EnvironmentState {
  selectedEnvironment: EnvironmentOption | null
  setSelectedEnvironment: Dispatch<SetStateAction<EnvironmentOption | null>>
  isAhpEnvironment: boolean
  isSandboxEnvironment: boolean
  showSandboxOption: boolean
  showEnvironmentPicker: boolean
}

export interface NewSessionComposerControllerProps {
  controller: NewSessionController
  configuration: NewSessionConfiguration
  targetState: TaskComposerTargetState
  draft: SharedSessionDraft
  branch: BranchState
  environment: EnvironmentState
  scope: TaskComposerScopeState
  initialTask?: {
    repository?: Repository
    repositoryNameWithOwner?: string
    reference?: CreateTaskReference
    branch?: string
  }
  initialPreparedPrompt?: PreparedPrompt
  initialChatProvisioningTarget?: AhpProvisioningTarget
  initialSpace?: AhpSpaceSelection
  initialBranch?: string
  initialTarget?: 'chat' | 'task'
  initialSubmission?: {
    id: string
    prompt: string
    directTurnTelemetrySeed?: DirectTurnTelemetrySeed
  }
  scopeControl?: ReactNode
  draftSource?: string
  /** Conditions the parent owns, such as an unresolved Chat destination. */
  forceDisabled: boolean
  readOnly: boolean
  emptyState?: 'embedded'
  containerClassName?: string
  disableWhileSubmitting: boolean
  submitWithModifier: boolean
  clearOnSubmit: boolean
  maxTextareaHeight?: number
  handleRef?: Ref<ComposerHandle>
  disableAutoFocus?: boolean
  onSubmittingChange: (isSubmitting: boolean) => void
  belowComposerContent?: ReactNode
}

/**
 * The shared composing surface for a session that has not started yet.
 *
 * One controller owns the draft, models, attachments, and submission behavior
 * while environment and target changes update the controls around the input.
 */
const rejectMissingTaskDestination: HandleUserSubmit = () => {
  throw new Error('This surface cannot create tasks, so the repository destination should not have been offered.')
}

export function NewSessionComposerController({
  controller: host,
  configuration,
  targetState,
  draft,
  branch,
  environment,
  scope,
  initialTask,
  initialPreparedPrompt,
  initialChatProvisioningTarget,
  initialSpace,
  initialBranch,
  initialTarget,
  initialSubmission,
  scopeControl,
  draftSource,
  forceDisabled,
  readOnly,
  emptyState,
  containerClassName,
  disableWhileSubmitting,
  submitWithModifier,
  clearOnSubmit,
  maxTextareaHeight,
  handleRef,
  disableAutoFocus,
  onSubmittingChange,
  belowComposerContent,
}: NewSessionComposerControllerProps) {
  const images = useMarkdownImageHandling({
    textAreaRef: targetState.textAreaRef,
    repositoryId: targetState.selectedRepo?.id?.toString(),
  })
  const chatLaunch = useMemo<ChatLaunch | undefined>(() => {
    if (host.chatLaunch?.kind === 'environment') {
      if (!host.createEnvironmentTask) return undefined
      return {
        kind: 'environment',
        ...(host.chatLaunch.availability ? {availability: host.chatLaunch.availability} : {}),
      }
    }

    const hostChatLaunch = host.chatLaunch
    const onSubmit = hostChatLaunch?.onSubmit
    if (!onSubmit) return undefined

    return {
      kind: 'handoff',
      ...(hostChatLaunch.submitRepositoryAsChat ? {submitRepositoryAsChat: true} : {}),
      onSubmit: async (prompt, metadata) => {
        const target: AhpProvisioningTarget | undefined = metadata?.space
          ? {kind: 'space', space: metadata.space}
          : metadata?.repositoryNameWithOwner
            ? {
                kind: 'repository',
                nameWithOwner: metadata.repositoryNameWithOwner,
                ...(metadata.scopeLogin ? {scopeOwner: metadata.scopeLogin} : {}),
              }
            : metadata?.scopeLogin
              ? {kind: 'scope', owner: metadata.scopeLogin}
              : undefined
        const request = createAhpChatStartRequestWithProvisioningTarget({prompt, target})
        // Handoffs may carry a genuinely explicit repository target in metadata,
        // but attached repository references remain prompt context only.
        await onSubmit(
          {
            ...request,
            ...(metadata?.repositoryNameWithOwner
              ? {metadata: {...request.metadata, repository: metadata.repositoryNameWithOwner}}
              : {}),
          },
          metadata?.directTurnTelemetrySeed,
        )
      },
    }
  }, [host.chatLaunch, host.createEnvironmentTask])
  const initialPrompt =
    initialTarget && initialPreparedPrompt ? {...initialPreparedPrompt, attachments: undefined} : initialPreparedPrompt
  const isSandboxModeDraft = isSandboxComposedDraft({
    isSandboxEnvironment: environment.isSandboxEnvironment,
    selectedEnvironmentKind: environment.selectedEnvironment?.kind,
    deliversByHandoff: chatLaunch?.kind === 'handoff',
  })
  const isSessionModeDraft = isSandboxModeDraft || environment.isAhpEnvironment
  const availableModes = availableAgentModes(isSessionModeDraft)
  const effectiveMode = resolveAvailableAgentMode(configuration.mode, isSessionModeDraft) ?? availableModes[0]
  const controller = useTaskComposer(
    {
      handleUserSubmit: host.createTask ?? rejectMissingTaskDestination,
      onEnvironmentSubmit: host.createEnvironmentTask,
      agentMode: configuration.mode ? effectiveMode : undefined,
      approvalMode: isSessionModeDraft ? configuration.approvalMode : undefined,
      initialRepo: initialTask?.repository,
      initialRepositoryNameWithOwner: initialTask?.repositoryNameWithOwner,
      initialReference: initialTask?.reference,
      initialText: draft.currentPrompt,
      initialPreparedPrompt: initialPrompt,
      initialTaskAttachments: initialTarget === 'task' ? initialPreparedPrompt?.attachments : undefined,
      initialChatAttachments: initialTarget === 'chat' ? initialPreparedPrompt?.attachments : undefined,
      initialChatProvisioningTarget: initialTarget === 'chat' ? initialChatProvisioningTarget : undefined,
      initialSpace: initialTarget === 'chat' ? initialSpace : undefined,
      chatLaunch,
      eventType: draftSource,
      supportsManagedSandbox: configuration.supportsManagedSandbox,
      agentQuotaSnapshot: configuration.agentQuota,
      mcpPermissionGateEnabled: configuration.mode === undefined ? configuration.mcpPermissionGateEnabled : false,
      currentUserLogin: configuration.currentUserLogin,
      currentUserAvatarUrl: configuration.currentUserAvatarUrl,
      availableOrganizations: configuration.availableOrganizations,
      hideRepoSelector: false,
      initialBranch,
      initialTarget,
      includeImplicitContextForTask: draftSource === 'quick_chat_panel',
      directTurnTelemetrySurface: configuration.directTurnTelemetrySurface,
      preserveDirectTurnTelemetryForHandoff: configuration.preserveDirectTurnTelemetryForHandoff,
    },
    {targetState, images, draft, branch, environment, scope},
  )
  useImperativeHandle(
    handleRef,
    () => ({
      addFiles: files => {
        if (controller.isAhpEnvironment) {
          void controller.environmentAttachments.handleFilesAdded(files, 'drop')
        } else {
          images.addFiles(files)
        }
      },
    }),
    [controller.environmentAttachments, controller.isAhpEnvironment, images],
  )
  const sessionModeRef = useRef<AgentMode>(configuration.mode ?? 'interactive')
  const actionsModeRef = useRef<AgentMode>(configuration.mode === 'autopilot' ? 'autopilot' : 'interactive')
  const previousSessionModeDraftRef = useRef(isSessionModeDraft)
  useEffect(() => {
    const mode = configuration.mode
    const onModeChange = configuration.onModeChange
    const wasSessionModeDraft = previousSessionModeDraftRef.current

    if (wasSessionModeDraft !== isSessionModeDraft) {
      if (mode) {
        if (wasSessionModeDraft) {
          sessionModeRef.current = mode
        } else if (mode !== 'plan') {
          actionsModeRef.current = mode
        }
      }

      previousSessionModeDraftRef.current = isSessionModeDraft
      const destinationMode = isSessionModeDraft ? sessionModeRef.current : actionsModeRef.current
      if (onModeChange && mode !== destinationMode) onModeChange(destinationMode)
      return
    }

    if (!mode) return
    if (isSessionModeDraft) {
      sessionModeRef.current = mode
    } else if (mode === 'plan') {
      onModeChange?.(actionsModeRef.current)
    } else {
      actionsModeRef.current = mode
    }
  }, [configuration.mode, configuration.onModeChange, isSessionModeDraft])
  const approval =
    isSessionModeDraft && configuration.approvalMode && configuration.onApprovalModeChange
      ? {
          selectedMode: configuration.approvalMode,
          availableModes: configuration.availableApprovalModes ?? APPROVAL_MODES,
          onModeChange: configuration.onApprovalModeChange,
        }
      : undefined
  const modePicker = configuration.modePicker?.({availableModes, approval})
  const leadingControl = scopeControl ? (
    <>
      {scopeControl}
      {modePicker}
    </>
  ) : (
    modePicker
  )

  useEffect(() => {
    onSubmittingChange(controller.isEnvironmentSubmitting)
    return () => onSubmittingChange(false)
  }, [onSubmittingChange, controller.isEnvironmentSubmitting])

  const disabled =
    forceDisabled ||
    controller.isBlockedByQuota ||
    controller.isEnvironmentSubmitting ||
    !controller.isRepositorySelectionValid ||
    controller.isImageUploading ||
    controller.isSpaceResolving ||
    controller.isScopeBlockedBySso ||
    (controller.isAhpEnvironment
      ? configuration.disableSandboxSubmission ||
        !environment.selectedEnvironment?.environmentId ||
        (controller.targetSelection.kind === 'chat' ? !chatLaunch : !host.createEnvironmentTask)
      : !controller.isModelSelectionReady)

  const submittedInitialIdRef = useRef<string | undefined>(undefined)
  const [initialSubmitError, setInitialSubmitError] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (
      !initialSubmission ||
      submittedInitialIdRef.current === initialSubmission.id ||
      initialSubmitError ||
      disabled ||
      draft.currentPrompt !== initialSubmission.prompt
    ) {
      return
    }

    submittedInitialIdRef.current = initialSubmission.id
    void (async () => {
      try {
        await controller.onSubmit(draft.currentPrompt, initialSubmission.directTurnTelemetrySeed, true)
      } catch (error) {
        submittedInitialIdRef.current = undefined
        const message = error instanceof ChatInputValidationError ? error.message : SUBMIT_ERROR_MESSAGE
        setInitialSubmitError(message)
        // The banner is the only visual report, so the task path announces it.
        // Sandbox Chat sends announce their own failure and never come here.
        announce(message)
      }
    })()
  }, [controller, disabled, draft.currentPrompt, initialSubmitError, initialSubmission])

  return (
    <>
      {/* Chronicle reads the cloud session store, so the menu is offered only
          where the session is actually granted that capability. The menu sits
          outside the textarea, so it also honors `readOnly`, which is what makes
          the composer reject edits. `disabled` is not checked here: it only
          reports that a submit is not yet possible (no repository picked, quota
          spent), while the prompt stays editable. */}
      <SlashCommandMenu
        suggestions={isCloudSessionStoreEnabled() && !readOnly ? controller.chronicleSuggestions : []}
        onSelect={controller.handleChronicleSelect}
        onDismiss={() => controller.setChronicleDismissed(true)}
        textAreaRef={targetState.textAreaRef}
        currentText={draft.currentPrompt}
        label="Chronicle command suggestions"
        idPrefix="new-session-chronicle"
      />
      <CopilotComposerInput
        controller={controller}
        value={draft.currentPrompt}
        onValueChange={controller.handleTextChange}
        textAreaRef={targetState.textAreaRef}
        disabled={disabled}
        readOnly={readOnly}
        emptyState={emptyState}
        containerClassName={containerClassName}
        submitWithModifier={submitWithModifier}
        clearOnSubmit={clearOnSubmit}
        disableWhileSubmitting={disableWhileSubmitting}
        maxTextareaHeight={maxTextareaHeight}
        disableAutoFocus={disableAutoFocus}
        responsiveIconOnly
        leadingControl={leadingControl}
        // A Sandbox task spends the same premium quota a Sandbox Chat does, so
        // the meter follows the draft rather than the destination.
        meter={
          isSessionTokenMeterApplicable({
            isTokenBasedBillingEnabled: controller.isTokenBasedBillingEnabled,
            isSandboxEnvironment: controller.isSandboxDraft,
          }) ? (
            <EntitlementTokenUsageMeter />
          ) : undefined
        }
        belowComposerContent={belowComposerContent}
      />
      {initialSubmitError && (
        <AttachmentErrorBanner
          messages={[initialSubmitError]}
          onDismiss={() => setInitialSubmitError(undefined)}
          title="Submission error"
        />
      )}
    </>
  )
}

NewSessionComposerController.displayName = 'NewSessionComposerController'
